/**
 * Hotel profile — view + edit the hotel's identity (name, languages, timezone,
 * status). Slug is immutable (the stable public id). The rich AI-facing profile
 * (description, amenities, policies) lives in the `hotel_profile` knowledge
 * category, linked from here. Editing requires `hotel:configure`; otherwise the
 * page is read-only. Data via `useHotel` / `useUpdateHotel` (provider-independent).
 */
import { type Hotel, useHotel, useUpdateHotel } from '@hostesa/api-client';
import { useAuth } from '@hostesa/auth-client';
import { useT } from '@hostesa/i18n';
import {
  Button,
  Card,
  CardContent,
  cn,
  ErrorState,
  Input,
  Label,
  LoadingState,
  PageHeader,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@hostesa/ui';
import { useState } from 'react';
import { useHotelContext } from '../../lib/hotel-context';
import { useImpersonationGuard } from '../../lib/impersonation-guard';

const PAGE_WRAP = 'flex flex-col gap-6';
const SUPPORTED_LANGUAGES = ['bg', 'en', 'de', 'fr', 'es', 'it'] as const;
const STATUSES = ['draft', 'active', 'suspended'] as const;

export function HotelProfilePage() {
  const { t } = useT('platform');
  const { tenantId, hotelId } = useHotelContext();
  const hotelQuery = useHotel(tenantId, hotelId);

  return (
    <section data-testid="hotel-profile-page" className={PAGE_WRAP}>
      <PageHeader
        eyebrow={t('hotelProfile.eyebrow')}
        title={t('hotelProfile.title')}
        description={t('hotelProfile.description')}
      />
      {hotelQuery.isLoading ? (
        <LoadingState data-testid="hotel-profile-loading" title={t('hotelProfile.loading')} />
      ) : hotelQuery.isError || !hotelQuery.data ? (
        <ErrorState
          data-testid="hotel-profile-error"
          title={t('hotelProfile.errorTitle')}
          description={t('hotelProfile.errorDescription')}
        />
      ) : (
        <HotelProfileForm key={hotelQuery.data.id} hotel={hotelQuery.data} />
      )}
    </section>
  );
}

function HotelProfileForm({ hotel }: { hotel: Hotel }) {
  const { t } = useT('platform');
  const { can } = useAuth();
  const { restricted } = useImpersonationGuard();
  const update = useUpdateHotel(hotel.tenantId, hotel.id);
  const editable = can('hotel:configure');

  const [name, setName] = useState(hotel.name);
  const [timezone, setTimezone] = useState(hotel.timezone);
  const [languages, setLanguages] = useState<string[]>(hotel.languages);
  const [defaultLanguage, setDefaultLanguage] = useState(hotel.defaultLanguage);
  const [status, setStatus] = useState<(typeof STATUSES)[number]>(
    STATUSES.includes(hotel.status as (typeof STATUSES)[number])
      ? (hotel.status as (typeof STATUSES)[number])
      : 'active',
  );

  const toggleLanguage = (lang: string) => {
    setLanguages((prev) => {
      const next = prev.includes(lang) ? prev.filter((l) => l !== lang) : [...prev, lang];
      // Keep the default language valid — fall back to the first remaining language.
      if (!next.includes(defaultLanguage) && next[0]) setDefaultLanguage(next[0]);
      return next;
    });
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Saving hotel settings is blocked during an impersonation session — the
    // backend rejects it too; this stops the operator firing it by reflex.
    if (!editable || restricted) return;
    update.mutate({ name: name.trim(), timezone, languages, defaultLanguage, status });
  };

  const disabled = !editable || restricted || update.isPending;

  return (
    <form data-testid="hotel-profile-form" onSubmit={onSubmit} className="flex flex-col gap-6">
      {!editable && (
        <p data-testid="hotel-profile-readonly" className="text-sm text-[var(--color-stone-mid)]">
          {t('hotelProfile.readOnlyNote')}
        </p>
      )}

      <Card>
        <CardContent className="flex flex-col gap-5 pt-6">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hotel-name">{t('hotelProfile.fields.name')}</Label>
            <Input
              id="hotel-name"
              data-testid="hotel-name-input"
              value={name}
              disabled={disabled}
              onChange={(e) => setName(e.target.value)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hotel-slug">{t('hotelProfile.fields.slug')}</Label>
            <Input id="hotel-slug" data-testid="hotel-slug" value={hotel.slug} disabled readOnly />
            <p className="text-xs text-[var(--color-stone-mid)]">
              {t('hotelProfile.fields.slugHint')}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hotel-timezone">{t('hotelProfile.fields.timezone')}</Label>
            <Input
              id="hotel-timezone"
              data-testid="hotel-timezone-input"
              value={timezone}
              disabled={disabled}
              onChange={(e) => setTimezone(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="flex flex-col gap-5 pt-6">
          <div className="flex flex-col gap-2">
            <Label>{t('hotelProfile.fields.languages')}</Label>
            <div className="flex flex-wrap gap-2" data-testid="hotel-languages">
              {SUPPORTED_LANGUAGES.map((lang) => {
                const on = languages.includes(lang);
                return (
                  <button
                    key={lang}
                    type="button"
                    data-testid={`hotel-language-${lang}`}
                    aria-pressed={on}
                    disabled={disabled}
                    onClick={() => toggleLanguage(lang)}
                    className={cn(
                      'rounded-full px-3 py-1 text-sm font-medium transition-colors disabled:opacity-50',
                      'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--color-ring)]',
                      on
                        ? 'bg-[var(--color-charcoal)] text-[var(--color-alabaster)]'
                        : 'bg-[var(--color-stone-100)] text-[var(--color-stone-700)] hover:bg-[var(--color-canvas)]',
                    )}
                  >
                    {lang.toUpperCase()}
                  </button>
                );
              })}
            </div>
            <p className="text-xs text-[var(--color-stone-mid)]">
              {t('hotelProfile.fields.languagesHint')}
            </p>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hotel-default-language">
              {t('hotelProfile.fields.defaultLanguage')}
            </Label>
            <Select
              value={defaultLanguage}
              onValueChange={setDefaultLanguage}
              disabled={disabled || languages.length === 0}
            >
              <SelectTrigger
                id="hotel-default-language"
                data-testid="hotel-default-language-trigger"
                className="h-9 w-40"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {languages.map((lang) => (
                  <SelectItem key={lang} value={lang} data-testid={`hotel-default-${lang}`}>
                    {lang.toUpperCase()}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="hotel-status">{t('hotelProfile.fields.status')}</Label>
            <Select
              value={status}
              onValueChange={(v) => setStatus(v as (typeof STATUSES)[number])}
              disabled={disabled}
            >
              <SelectTrigger
                id="hotel-status"
                data-testid="hotel-status-trigger"
                className="h-9 w-44"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {STATUSES.map((s) => (
                  <SelectItem key={s} value={s} data-testid={`hotel-status-${s}`}>
                    {t(`hotelProfile.status.${s}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-0.5">
            <span className="text-xs uppercase tracking-wide text-[var(--color-stone-mid)]">
              {t('hotelProfile.fields.operationsAgent')}
            </span>
            <span
              data-testid="hotel-operations-agent"
              className="text-sm text-[var(--color-charcoal)]"
            >
              {hotel.operationsAgentEnabled
                ? t('hotelProfile.operationsAgent.enabled')
                : t('hotelProfile.operationsAgent.disabled')}
            </span>
          </div>
        </CardContent>
      </Card>

      <p className="text-sm text-[var(--color-stone-mid)]">{t('hotelProfile.knowledgeLink')}</p>

      {editable && restricted && (
        <p
          data-testid="hotel-profile-impersonation-block"
          role="note"
          className="text-sm text-[var(--color-stone-mid)]"
        >
          {t('impersonationRestriction')}
        </p>
      )}

      {editable && (
        <div className="flex items-center gap-3">
          <Button type="submit" data-testid="hotel-profile-save-btn" disabled={disabled}>
            {update.isPending ? t('hotelProfile.saving') : t('hotelProfile.save')}
          </Button>
          {update.isSuccess && (
            <span data-testid="hotel-profile-saved" className="text-sm text-[var(--color-success)]">
              {t('hotelProfile.saved')}
            </span>
          )}
          {update.isError && (
            <span
              data-testid="hotel-profile-save-error"
              className="text-sm text-[var(--color-alert)]"
            >
              {t('hotelProfile.saveError')}
            </span>
          )}
        </div>
      )}
    </form>
  );
}
